import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { theme, Tabs, Button } from "antd";
import MediaThumbnailGrid, { ThumbnailItem } from "./MediaThumbnailGrid";
import { GrossImage, Photo } from "../../api/postAPI";
import { Slide } from "../../api/slideAPI";
import { MediaGroup, MediaType } from "../../api/mediaGroupAPI";
import { GroupCounts, GroupFilter, groupIdOf } from "./mediaGroup";
import { PostVideo, getVideoFileUrl, getVideoThumbnailUrl } from "../../api/videoAPI";

const getPhotoUrl = (path: string): string =>
  `${process.env.REACT_APP_BACKEND_URL}/api/photo/${path}`;

const getGrossPhotoUrl = (path: string): string =>
  `${process.env.REACT_APP_BACKEND_URL}/api/gross-photo/file/${path}`;

export interface MediaGroupBinding {
  groups: MediaGroup[];
  counts: GroupCounts;
  active: GroupFilter;
  onFilterChange: (next: GroupFilter) => void;
  onCreate: (name: string) => Promise<void>;
  onRename: (mediaGroupId: number, name: string) => Promise<void>;
  onDelete: (mediaGroupId: number) => Promise<void>;
  onMove: (mediaIds: number[], targetGroupId: number | null, newGroupName?: string) => Promise<void>;
  /** 그룹 편집 — 체크된 목록이 그 그룹의 새 멤버 전체가 된다 */
  onEditMembers: (mediaGroupId: number, mediaIds: number[]) => Promise<void>;
}

interface PostMediaTabPanelProps {
  slides: Slide[];
  imageList: Photo[];
  grossImageList: GrossImage[];
  currentIndex: number;
  isSlideFile: boolean;
  isGrossFile: boolean;
  hasEditAuth: boolean;
  onSlideSelect: (index: number) => void;
  onImageSelect: (index: number) => void;
  onGrossImageSelect: (index: number) => void;
  onOpenSlideEditModal: () => void;
  onOpenImageEditModal: () => void;
  onOpenGrossImageEditModal: () => void;
  onShowAddSlideModal: () => void;
  onShowAddImageModal: () => void;
  onShowAddGrossImageModal: () => void;
  slideOnlyMode?: boolean;
  // 썸네일 우클릭 이름 수정 (index 는 각 목록 기준)
  onRenameSlide?: (index: number, newName: string) => Promise<void> | void;
  onRenameImage?: (index: number, newName: string) => Promise<void> | void;
  onRenameGross?: (index: number, newName: string) => Promise<void> | void;
  /** 미디어 그룹 (mediaType 별). 없으면 해당 탭의 그룹 UI 미노출 */
  groupBindings?: Partial<Record<MediaType, MediaGroupBinding>>;
  /** 동영상 탭 — onVideoSelect 를 넘길 때만 노출 */
  videoList?: PostVideo[];
  isVideoFile?: boolean;
  onVideoSelect?: (index: number) => void;
  onShowAddVideoModal?: () => void;
  /** Slide 탭의 'Video 탭 보기' 버튼 — 넘길 때(관리자)만, 탭이 숨겨져 있을 때만 노출 */
  isVideoTabVisible?: boolean;
  onToggleVideoTab?: () => void;
}

interface PanelWrapperProps {
  $bgColor: string;
  $borderRadius: number;
  $borderColor: string;
}

const PostMediaTabPanel = ({
  slides,
  imageList,
  grossImageList,
  currentIndex,
  isSlideFile,
  isGrossFile,
  hasEditAuth,
  onSlideSelect,
  onImageSelect,
  onGrossImageSelect,
  onOpenSlideEditModal,
  onOpenImageEditModal,
  onOpenGrossImageEditModal,
  onShowAddSlideModal,
  onShowAddImageModal,
  onShowAddGrossImageModal,
  slideOnlyMode = false,
  onRenameSlide,
  onRenameImage,
  onRenameGross,
  groupBindings,
  videoList = [],
  isVideoFile = false,
  onVideoSelect,
  onShowAddVideoModal,
  isVideoTabVisible = false,
  onToggleVideoTab,
}: PostMediaTabPanelProps) => {
  const { token } = theme.useToken();

  const computeActiveTab = () => {
    if (isVideoFile) return "video";
    if (isSlideFile) return "slide";
    if (isGrossFile) return "gross";
    return "image";
  };

  const [activeTab, setActiveTab] = useState<string>(computeActiveTab());

  useEffect(() => {
    setActiveTab(computeActiveTab());
  }, [isSlideFile, isGrossFile, isVideoFile]);

  const slideItems: ThumbnailItem[] = useMemo(
    () =>
      slides.map((slide: any, i) => ({
        key: slide.id ?? `${slide.filename}_${i}`,
        thumbnailUrl: getPhotoUrl(slide.thumbnail),
        name: slide.slidename,
        mediaId: slide.slideId,
        mediaGroupId: groupIdOf(slide),
        qcResult: slide.qcResult ?? null,
      })),
    [slides]
  );

  const imageItems: ThumbnailItem[] = useMemo(
    () =>
      imageList.map((image: any, i) => ({
        key: image.id ?? `${image.pathFileName}_${i}`,
        thumbnailUrl: getPhotoUrl(image.pathFileName),
        name: image.imageName,
        mediaId: image.photoId,
        mediaGroupId: groupIdOf(image),
      })),
    [imageList]
  );

  const grossImageItems: ThumbnailItem[] = useMemo(
    () =>
      grossImageList.map((image: any, i) => ({
        key: image.grossPhotoId ?? `${image.pathFileName}_${i}`,
        thumbnailUrl: getGrossPhotoUrl(image.pathFileName),
        name: image.caption,
        mediaId: image.grossPhotoId,
        mediaGroupId: groupIdOf(image),
      })),
    [grossImageList]
  );

  const videoItems: ThumbnailItem[] = useMemo(
    () =>
      videoList.map((video, i) => ({
        key: String(video.videoId ?? `${video.pathFileName}_${i}`),
        // 썸네일이 없으면 MediaThumbnailGrid 가 videoUrl 의 첫 프레임을 보여준다
        thumbnailUrl: getVideoThumbnailUrl(video) ?? "",
        videoUrl: getVideoFileUrl(video),
        name: video.videoName,
        mediaId: video.videoId,
      })),
    [videoList]
  );

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === "slide") onSlideSelect(0);
    else if (key === "image") onImageSelect(0);
    else if (key === "gross") onGrossImageSelect(0);
    else if (key === "video") onVideoSelect?.(0);
  };

  const groupProps = (type: MediaType, itemLabel: string) => {
    const binding = groupBindings?.[type];
    if (!binding) return {};
    return {
      groups: binding.groups,
      groupCounts: binding.counts,
      activeGroup: binding.active,
      onGroupFilterChange: binding.onFilterChange,
      itemLabel,
      onCreateGroup: binding.onCreate,
      onRenameGroup: binding.onRename,
      onDeleteGroup: binding.onDelete,
      onMoveToGroup: binding.onMove,
      onEditGroupMembers: binding.onEditMembers,
    };
  };

  const tabItems = [
    {
      key: "gross",
      label: `Gross (${grossImageList.length})`,
      children: (
        <MediaThumbnailGrid
          title="Gross Image List"
          showTitle={false}
          items={grossImageItems}
          currentIndex={currentIndex}
          isActive={isGrossFile}
          hasEditAuth={hasEditAuth}
          onSelect={onGrossImageSelect}
          onOpenEditModal={onOpenGrossImageEditModal}
          onOpenAddModal={onShowAddGrossImageModal}
          onRename={onRenameGross}
          size="large"
          {...groupProps("GROSS", "그로스 이미지")}
        />
      ),
    },
    {
      key: "slide",
      label: `Slide (${slides.length})`,
      children: (
        <MediaThumbnailGrid
          title="Slide List"
          showTitle={false}
          items={slideItems}
          currentIndex={currentIndex}
          isActive={isSlideFile}
          hasEditAuth={hasEditAuth}
          onSelect={onSlideSelect}
          onOpenEditModal={onOpenSlideEditModal}
          onOpenAddModal={onShowAddSlideModal}
          onRename={onRenameSlide}
          size="large"
          {...groupProps("SLIDE", "슬라이드")}
          extraActions={
            onToggleVideoTab &&
            !isVideoTabVisible && (
              <Button size="small" variant="outlined" onClick={onToggleVideoTab}>
                Video 탭 보기
              </Button>
            )
          }
        />
      ),
    },
    {
      key: "image",
      label: `Image (${imageList.length})`,
      children: (
        <MediaThumbnailGrid
          title="Image List"
          showTitle={false}
          items={imageItems}
          currentIndex={currentIndex}
          isActive={!isSlideFile && !isGrossFile && !isVideoFile}
          hasEditAuth={hasEditAuth}
          onSelect={onImageSelect}
          onOpenEditModal={onOpenImageEditModal}
          onOpenAddModal={onShowAddImageModal}
          onRename={onRenameImage}
          size="large"
          {...groupProps("PHOTO", "이미지")}
        />
      ),
    },
    // 동영상 — 그룹·이름 수정·편집(순서/삭제)은 BE API 준비 후 연결
    ...(onVideoSelect
      ? [
          {
            key: "video",
            label: `Video (${videoList.length})`,
            children: (
              <MediaThumbnailGrid
                title="Video List"
                showTitle={false}
                items={videoItems}
                currentIndex={currentIndex}
                isActive={isVideoFile}
                hasEditAuth={hasEditAuth}
                onSelect={onVideoSelect}
                onOpenAddModal={onShowAddVideoModal ?? (() => {})}
                size="large"
              />
            ),
          },
        ]
      : []),
  ];

  if (slideOnlyMode) {
    return (
      <PanelWrapper
        $bgColor={token.colorBgContainer}
        $borderRadius={token.borderRadius}
        $borderColor={token.colorBorder}
      >
        <MediaThumbnailGrid
          title=""
          items={slideItems}
          currentIndex={currentIndex}
          isActive={true}
          hasEditAuth={hasEditAuth}
          onSelect={onSlideSelect}
          onOpenEditModal={onOpenSlideEditModal}
          onOpenAddModal={onShowAddSlideModal}
          onRename={onRenameSlide}
          size="large"
          {...groupProps("SLIDE", "슬라이드")}
        />
      </PanelWrapper>
    );
  }

  return (
    <PanelWrapper
      $bgColor={token.colorBgContainer}
      $borderRadius={token.borderRadius}
      $borderColor={token.colorBorder}
    >
      <StyledTabs
        activeKey={activeTab}
        items={tabItems}
        onChange={handleTabChange}
        size="small"
        tabPosition="left"
      />
    </PanelWrapper>
  );
};

const PanelWrapper = styled.div<PanelWrapperProps>`
  background-color: ${({ $bgColor }) => $bgColor};
  border-right-width: 0;
  border-left-width: 0;
  border-top-width: 0;
  padding: 5px 8px;
  overflow-y: auto;
  overflow-x: hidden;
  flex-shrink: 0;
`;

const StyledTabs = styled(Tabs)`
  /* 좌측 세로 탭 */
  .ant-tabs-nav {
    min-width: 110px;
    margin-right: 12px;
  }

  .ant-tabs-tab {
    font-size: 0.85rem;
    font-weight: 500;
    justify-content: flex-start !important;
    padding: 8px 12px !important;
  }

  .ant-tabs-tab + .ant-tabs-tab {
    margin-top: 4px !important;
  }

  .ant-tabs-content-holder {
    overflow: visible;
    border-left: 1px solid #f0f0f0;
    padding-left: 12px;
  }
`;

export default PostMediaTabPanel;
